import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { useProperties } from "@/contexts/PropertyContext";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { Modal, Field } from "./Rooms";
import {
    Plus, Minus, Trash2, Receipt, Wand2, Settings, BarChart3, Search,
    Coffee, Wine, Utensils, Sparkles, Bell, Briefcase, X, BedDouble,
    SplitSquareVertical, Printer, History,
} from "lucide-react";
import { toast } from "sonner";
import PosHistoryDrawer from "@/components/pos/PosHistoryDrawer";

// ------------------------------------------------------------
// Outlet meta (icons + labels)
// ------------------------------------------------------------
const OUTLET_META = {
    restaurant:      { icon: Utensils },
    bar:             { icon: Wine },
    minibar:         { icon: Coffee },
    spa:             { icon: Sparkles },
    room_service:    { icon: Bell },
    extra_services:  { icon: Briefcase },
};
const OUTLET_KEYS = ["restaurant", "bar", "minibar", "spa", "room_service", "extra_services"];

const PAYMENT_METHOD_KEYS = ["cash", "card", "room_charge", "bank_transfer"];

export default function POS() {
    const { t } = useTranslation();
    const { properties, activePropertyId } = useProperties();
    const [outlet, setOutlet] = useState("restaurant");
    const [products, setProducts] = useState([]);
    const [tables, setTables] = useState([]);
    const [activeRes, setActiveRes] = useState([]);
    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [ordersError, setOrdersError] = useState(false);
    const [report, setReport] = useState(null);
    const [cart, setCart] = useState([]);
    const [discount, setDiscount] = useState(0);
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("All");
    const [paymentMethod, setPaymentMethod] = useState("cash");
    const [splitMode, setSplitMode] = useState(false);
    const [splits, setSplits] = useState([{ method: "cash", amount: 0, reservation_id: "" }]);
    const [tableSel, setTableSel] = useState("");
    const [resSel, setResSel] = useState(null);   // {id, room_number, guest_name}
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [invoice, setInvoice] = useState(null);

    const propertyId = activePropertyId !== "all" ? activePropertyId : properties[0]?.id;

    // Load each POS resource independently so one failure doesn't wipe the
    // rest of the page (e.g. a broken /reports/daily was blanking the orders
    // list before). Orders in particular MUST always come from the backend
    // — never trust in-memory state — so we track loading/error separately.
    const reload = async () => {
        setOrdersLoading(true);
        setOrdersError(false);
        const safe = (p) => p.catch((e) => { console.error("[POS] load failed:", e); return null; });
        const [p, tv, ar, o, r] = await Promise.all([
            safe(api.get(`/pos/products?active=true${outlet ? `&outlet=${outlet}` : ""}`)),
            safe(api.get(`/pos/tables?outlet=${outlet}`)),
            safe(api.get("/pos/active-reservations")),
            safe(api.get(`/pos/orders?outlet=${outlet}&limit=20`)),
            safe(api.get("/pos/reports/daily")),
        ]);
        if (p) setProducts(p.data);
        if (tv) setTables(tv.data);
        if (ar) setActiveRes(ar.data);
        if (o) {
            setOrders(o.data);
        } else {
            setOrdersError(true);
        }
        if (r) setReport(r.data);
        setOrdersLoading(false);
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [outlet]);

    const seed = async () => {
        try {
            const { data } = await api.post("/pos/seed-defaults");
            toast.success(data.skipped ? t("pos.seeded_already") : t("pos.seeded_msg", { p: data.products, t: data.tables }));
            await reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    // ------------- cart ops -------------
    const add = (p) => {
        setCart((c) => {
            const idx = c.findIndex((x) => x.product_id === p.id);
            if (idx >= 0) {
                const copy = [...c];
                copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + 1 };
                return copy;
            }
            return [...c, { product_id: p.id, name: p.name, price: p.price, quantity: 1, category: p.category, sku: p.sku, taxable: p.taxable }];
        });
    };
    const inc = (i) => setCart((c) => c.map((x, k) => k === i ? { ...x, quantity: x.quantity + 1 } : x));
    const dec = (i) => setCart((c) => c.map((x, k) => k === i ? { ...x, quantity: x.quantity - 1 } : x).filter((x) => x.quantity > 0));
    const del = (i) => setCart((c) => c.filter((_, k) => k !== i));

    const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.quantity, 0), [cart]);
    const discountSafe = Math.min(discount || 0, subtotal);
    const taxable = Math.max(0, subtotal - discountSafe);
    const tax = +(taxable * 0.10).toFixed(2);
    const total = +(taxable + tax).toFixed(2);

    const categories = useMemo(() => ["All", ...Array.from(new Set(products.map((p) => p.category)))], [products]);
    const filtered = useMemo(() => products.filter((p) =>
        (category === "All" || p.category === category) &&
        (!search || p.name.toLowerCase().includes(search.toLowerCase()))
    ), [products, category, search]);

    // ------------- split logic -------------
    const setSplitField = (i, k, v) => setSplits((s) => {
        const c = [...s]; c[i] = { ...c[i], [k]: v }; return c;
    });
    const splitsTotal = useMemo(() => splits.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0), [splits]);
    const splitRemaining = Math.max(0, total - splitsTotal);

    // ------------- checkout -------------
    const checkout = async () => {
        if (!cart.length) return;
        if (!propertyId) { toast.error("Select a property first"); return; }

        let payments = [];
        if (splitMode) {
            const cleaned = splits.filter((p) => parseFloat(p.amount) > 0).map((p) => ({
                method: p.method, amount: parseFloat(p.amount),
                reservation_id: p.method === "room_charge" ? (p.reservation_id || resSel?.id) : undefined,
            }));
            if (cleaned.length === 0) {
                toast.error("Add at least one split payment");
                return;
            }
            if (Math.abs(cleaned.reduce((s, p) => s + p.amount, 0) - total) > 0.01) {
                toast.error(`Split total must equal ${formatCurrency(total)}`);
                return;
            }
            payments = cleaned;
        } else {
            payments = [{
                method: paymentMethod, amount: total,
                reservation_id: paymentMethod === "room_charge" ? resSel?.id : undefined,
            }];
            if (paymentMethod === "room_charge" && !resSel) {
                toast.error("Select an active reservation for room charge");
                return;
            }
        }

        try {
            // Per-checkout idempotency key — regenerated after success so a
            // legitimate second sale for the same cart still goes through.
            const clientOrderId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
            const { data } = await api.post("/pos/orders", {
                property_id: propertyId,
                outlet,
                client_order_id: clientOrderId,
                items: cart,
                payments,
                payment_method: payments[0].method,
                table_id: tableSel || null,
                table_number: tableSel ? tables.find((t) => t.id === tableSel)?.name : null,
                reservation_id: resSel?.id || null,
                guest_id: resSel?.guest_id || null,
                room_id: resSel?.room_id || null,
                discount_amount: discountSafe,
                status: "paid",
            });
            toast.success(t("pos.order_placed", { inv: data.invoice_number, total: formatCurrency(data.total) }));
            setCart([]); setDiscount(0); setSplitMode(false);
            setSplits([{ method: "cash", amount: 0, reservation_id: "" }]);
            setTableSel(""); setResSel(null); setPaymentMethod("cash");
            // Auto-show invoice
            const { data: inv } = await api.get(`/pos/orders/${data.id}/invoice`);
            setInvoice(inv);
            await reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <>
            <Topbar
                title={t("pos.page_title")}
                subtitle={subtitleFor(report, outlet, t)}
                actions={
                    <div className="flex items-center gap-2">
                        <button onClick={() => setHistoryOpen(true)} className="akm-btn-ghost text-[11px]" data-testid="pos-history-open">
                            <History size={12} /> {t("pos.history", { defaultValue: "Historiku" })}
                        </button>
                        <button onClick={seed} className="akm-btn-ghost text-[11px]" data-testid="pos-seed">
                            <Wand2 size={12} /> {t("pos.seed_catalog")}
                        </button>
                        <button onClick={() => setSettingsOpen(true)} className="akm-btn-ghost text-[11px]" data-testid="pos-settings">
                            <Settings size={12} /> {t("pos.manage")}
                        </button>
                    </div>
                }
            />

            <div className="flex-1 overflow-y-auto p-4 md:p-6">
                {/* Outlet selector */}
                <div className="mb-3 flex flex-wrap gap-1" data-testid="pos-outlets">
                    {OUTLET_KEYS.map((k) => {
                        const Icon = OUTLET_META[k].icon;
                        const isActive = outlet === k;
                        return (
                            <button key={k} onClick={() => setOutlet(k)}
                                    data-testid={`pos-outlet-${k}`}
                                    className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[12px] font-medium transition ${
                                        isActive ? "border-[#ff7b02] bg-[#ff7b02] text-white" : "border-border bg-card hover:bg-muted/40"
                                    }`}>
                                <Icon size={12} /> {t(`pos.outlets.${k}`)}
                            </button>
                        );
                    })}
                </div>

                <div className="grid h-[calc(100vh-220px)] grid-cols-1 gap-3 lg:grid-cols-3">
                    {/* Catalog */}
                    <div className="akm-card flex min-h-0 flex-col p-3 lg:col-span-2">
                        <div className="mb-2 flex items-center gap-2">
                            <div className="relative flex-1">
                                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <input value={search} onChange={(e) => setSearch(e.target.value)}
                                       placeholder={t("pos.search_products")}
                                       className="akm-input pl-7 text-[12px]"
                                       data-testid="pos-search" />
                            </div>
                            <div className="text-[11px] text-muted-foreground">{t("pos.items_count", { count: filtered.length })}</div>
                        </div>
                        <div className="mb-2 flex flex-wrap gap-1">
                            {categories.map((c) => (
                                <button key={c} onClick={() => setCategory(c)}
                                        data-testid={`pos-cat-${c.toLowerCase()}`}
                                        className={`rounded-md border px-2.5 py-1 text-[11px] font-medium ${
                                            category === c ? "border-[#ff7b02] bg-[#ff7b02]/10 text-[#ff7b02]" : "border-border bg-card hover:bg-muted/40"
                                        }`}>
                                    {c}
                                </button>
                            ))}
                        </div>
                        <div className="grid flex-1 grid-cols-2 gap-2 overflow-auto pr-1 sm:grid-cols-3 md:grid-cols-4">
                            {filtered.map((p) => (
                                <button key={p.id} onClick={() => add(p)}
                                        data-testid={`pos-product-${p.id}`}
                                        className="flex flex-col items-start rounded-md border border-border bg-background p-2.5 text-left transition hover:border-[#ff7b02]/40 hover:bg-[#ff7b02]/5">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{p.category}</div>
                                    <div className="mt-0.5 text-[13px] font-medium leading-snug">{p.name}</div>
                                    <div className="mt-1 text-[13px] font-semibold text-[#ff7b02]">{formatCurrency(p.price)}</div>
                                </button>
                            ))}
                            {filtered.length === 0 && (
                                <div className="col-span-full py-12 text-center text-[12px] text-muted-foreground">
                                    {t("pos.no_products_hint")}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Cart + checkout */}
                    <div className="akm-card flex min-h-0 flex-col p-3" data-testid="pos-cart">
                        <div className="mb-2 flex items-center justify-between">
                            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t("pos.current_order")}</div>
                            {cart.length > 0 && (
                                <button onClick={() => setCart([])} className="text-[11px] text-red-600 hover:underline">{t("pos.clear")}</button>
                            )}
                        </div>

                        {/* Table + Room selectors */}
                        <div className="mb-2 grid grid-cols-2 gap-1.5">
                            <select className="akm-input text-[12px]" value={tableSel}
                                    onChange={(e) => setTableSel(e.target.value)}
                                    data-testid="pos-table-select">
                                <option value="">{t("pos.no_table")}</option>
                                {tables.map((tb) => (
                                    <option key={tb.id} value={tb.id}>{tb.name} · {tb.location || "—"}</option>
                                ))}
                            </select>
                            <select className="akm-input text-[12px]" value={resSel?.id || ""}
                                    onChange={(e) => {
                                        const r = activeRes.find((x) => x.id === e.target.value);
                                        setResSel(r || null);
                                    }}
                                    data-testid="pos-reservation-select">
                                <option value="">{t("pos.no_room")}</option>
                                {activeRes.map((r) => (
                                    <option key={r.id} value={r.id}>
                                        {t("pos.room_prefix")} {r.room_number} · {r.guest_first_name} {r.guest_last_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Items list */}
                        <div className="flex-1 space-y-1.5 overflow-auto pr-1">
                            {cart.map((it, i) => (
                                <div key={i} className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1.5">
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-[12px] font-medium">{it.name}</div>
                                        <div className="text-[10px] text-muted-foreground">{formatCurrency(it.price)} · {it.category}</div>
                                    </div>
                                    <button onClick={() => dec(i)} className="rounded-md border border-border p-1"><Minus size={10} /></button>
                                    <div className="w-5 text-center text-[12px] font-semibold">{it.quantity}</div>
                                    <button onClick={() => inc(i)} className="rounded-md border border-border p-1"><Plus size={10} /></button>
                                    <button onClick={() => del(i)} className="rounded-md p-1 text-red-600 hover:bg-red-50"><Trash2 size={10} /></button>
                                </div>
                            ))}
                            {cart.length === 0 && (
                                <div className="py-8 text-center text-[12px] text-muted-foreground">{t("pos.tap_to_add")}</div>
                            )}
                        </div>

                        {/* Totals */}
                        <div className="mt-2 space-y-0.5 border-t border-border pt-2 text-[12px]">
                            <Row label={t("pos.subtotal_label")} value={formatCurrency(subtotal)} />
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">{t("pos.discount_label")}</span>
                                <input type="number" min="0" max={subtotal} value={discount}
                                       onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                                       className="akm-input h-6 w-20 text-right text-[12px]"
                                       data-testid="pos-discount" />
                            </div>
                            <Row label={t("pos.tax_10_label")} value={formatCurrency(tax)} />
                            <Row label={t("pos.total_label")} value={formatCurrency(total)} bold />
                        </div>

                        {/* Split toggle */}
                        <label className="mt-2 flex items-center gap-2 text-[11px]">
                            <input type="checkbox" checked={splitMode}
                                   onChange={(e) => {
                                       setSplitMode(e.target.checked);
                                       if (e.target.checked) {
                                           setSplits([
                                               { method: "cash", amount: (total / 2).toFixed(2), reservation_id: "" },
                                               { method: "card", amount: (total / 2).toFixed(2), reservation_id: "" },
                                           ]);
                                       }
                                   }}
                                   data-testid="pos-split-toggle" />
                            <SplitSquareVertical size={11} /> {t("pos.split_bill")}
                        </label>

                        {/* Payment area */}
                        {splitMode ? (
                            <div className="mt-1.5 space-y-1.5">
                                {splits.map((p, i) => (
                                    <div key={i} className="grid grid-cols-[1fr_80px_24px] gap-1 items-center">
                                        <select className="akm-input h-7 text-[11px]" value={p.method}
                                                onChange={(e) => setSplitField(i, "method", e.target.value)}
                                                data-testid={`pos-split-method-${i}`}>
                                            {PAYMENT_METHOD_KEYS.map((mk) => <option key={mk} value={mk}>{t(`pos.payment.${mk}`)}</option>)}
                                        </select>
                                        <input type="number" min="0" step="0.01" className="akm-input h-7 text-right text-[11px]"
                                               value={p.amount}
                                               onChange={(e) => setSplitField(i, "amount", e.target.value)}
                                               data-testid={`pos-split-amount-${i}`} />
                                        <button onClick={() => setSplits((s) => s.filter((_, k) => k !== i))}
                                                className="rounded-md text-red-600 hover:bg-red-50"><X size={12} /></button>
                                    </div>
                                ))}
                                <button onClick={() => setSplits((s) => [...s, { method: "cash", amount: splitRemaining.toFixed(2), reservation_id: "" }])}
                                        className="akm-btn-ghost text-[11px] w-full">{t("pos.add_payment")}</button>
                                <div className="text-[11px] text-muted-foreground text-right">
                                    {t("pos.split_progress", { paid: splitsTotal.toFixed(2), total: total.toFixed(2), remaining: splitRemaining.toFixed(2) })}
                                </div>
                            </div>
                        ) : (
                            <div className="mt-1.5 grid grid-cols-2 gap-1">
                                {PAYMENT_METHOD_KEYS.map((mk) => (
                                    <button key={mk} onClick={() => setPaymentMethod(mk)}
                                            data-testid={`pos-pay-${mk}`}
                                            className={`flex items-center justify-center gap-1 rounded-md border px-2 py-1.5 text-[11px] font-medium ${
                                                paymentMethod === mk ? "border-[#ff7b02] bg-[#ff7b02] text-white" : "border-border bg-card hover:bg-muted/40"
                                            }`}>
                                        {mk === "room_charge" && <BedDouble size={11} />}
                                        {t(`pos.payment.${mk}`)}
                                    </button>
                                ))}
                            </div>
                        )}

                        <button onClick={checkout} disabled={!cart.length}
                                data-testid="pos-checkout"
                                className="mt-2 flex items-center justify-center gap-2 rounded-md bg-[#ff7b02] py-2.5 text-[13px] font-semibold text-white disabled:opacity-50 hover:bg-[#cc6202]">
                            <Receipt size={13} /> {t("pos.charge_total", { amount: formatCurrency(total) })}
                        </button>
                    </div>
                </div>

                {/* Recent orders + daily report */}
                <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
                    <div className="akm-card p-3 lg:col-span-2" data-testid="pos-recent">
                        <div className="mb-2 flex items-center justify-between">
                            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t("pos.recent_orders")} · {t(`pos.outlets.${outlet}`)}</div>
                            <div className="text-[11px] text-muted-foreground">{orders.length}</div>
                        </div>
                        <div className="space-y-1" data-testid="pos-recent-list">
                            {ordersLoading && orders.length === 0 && (
                                <div className="space-y-1" data-testid="pos-recent-loading">
                                    {[0, 1, 2].map((i) => (
                                        <div key={i} className="h-8 animate-pulse rounded-md bg-muted/50" />
                                    ))}
                                </div>
                            )}
                            {!ordersLoading && ordersError && orders.length === 0 && (
                                <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200" data-testid="pos-recent-error">
                                    {t("pos.orders_load_failed", "Ngarkimi i porosive dështoi.")}{" "}
                                    <button type="button" onClick={reload} className="underline hover:no-underline">
                                        {t("common.retry", "Provo sërish")}
                                    </button>
                                </div>
                            )}
                            {orders.slice(0, 8).map((o) => (
                                <div key={o.id} className="flex items-center justify-between rounded-md border border-border px-2 py-1.5 text-[12px]"
                                     data-testid={`pos-order-${o.id}`}>
                                    <div className="min-w-0 flex-1 truncate">
                                        <span className="font-mono text-[11px] text-muted-foreground">{o.invoice_number || o.id.slice(0, 8)}</span>
                                        <span className="ml-2">{o.items.length} item{o.items.length > 1 ? "s" : ""}</span>
                                        {o.table_number && <span className="ml-2 text-muted-foreground">· {o.table_number}</span>}
                                        {o.payments?.some((p) => p.method === "room_charge") && (
                                            <span className="ml-2 rounded-full bg-amber-50 px-1.5 py-0 text-[10px] text-amber-700">{t("pos.room_charge_badge")}</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold">{formatCurrency(o.total)}</span>
                                        <button onClick={async () => {
                                            const { data } = await api.get(`/pos/orders/${o.id}/invoice`);
                                            setInvoice(data);
                                        }} className="akm-btn-ghost text-[10px]" data-testid={`pos-invoice-${o.id}`}>
                                            <Printer size={10} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {!ordersLoading && !ordersError && orders.length === 0 && (
                                <div className="py-4 text-center text-[12px] text-muted-foreground" data-testid="pos-no-orders">{t("pos.no_orders_today")}</div>
                            )}
                        </div>
                    </div>
                    {report && <DailyReport report={report} />}
                </div>
            </div>

            {invoice && <InvoiceModal invoice={invoice} onClose={() => setInvoice(null)} />}
            {settingsOpen && <ManageDrawer products={products} tables={tables} reload={reload} onClose={() => setSettingsOpen(false)} />}
            {historyOpen && (
                <PosHistoryDrawer
                    outlet={outlet}
                    canVoid={true}
                    onReprint={(inv) => setInvoice(inv)}
                    onClose={() => setHistoryOpen(false)}
                />
            )}
        </>
    );
}

function subtitleFor(report, outlet, t) {
    if (!report) return t("pos.loading");
    return t("pos.todays_subtitle", { orders: report.orders, total: formatCurrency(report.total), outlet: t(`pos.outlets.${outlet}`) });
}

function Row({ label, value, bold }) {
    return (
        <div className={`flex justify-between ${bold ? "text-foreground font-semibold" : "text-muted-foreground"}`}>
            <span>{label}</span>
            <span>{value}</span>
        </div>
    );
}

// ------------------------------------------------------------
// Daily sales report card
// ------------------------------------------------------------
function DailyReport({ report }) {
    return (
        <div className="akm-card p-3" data-testid="pos-daily-report">
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <BarChart3 size={11} /> Daily sales · {report.date}
            </div>
            <div className="mb-2 grid grid-cols-2 gap-2">
                <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Total</div>
                    <div className="text-lg font-semibold text-[#ff7b02]">{formatCurrency(report.total)}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Orders</div>
                    <div className="text-lg font-semibold">{report.orders}</div>
                </div>
            </div>
            {report.by_outlet.length > 0 && (
                <div>
                    <div className="mb-1 text-[10px] uppercase text-muted-foreground">By outlet</div>
                    {report.by_outlet.map((b) => (
                        <div key={b.outlet} className="flex justify-between text-[11px]">
                            <span>{b.label}</span>
                            <span className="font-medium">{formatCurrency(b.total)}</span>
                        </div>
                    ))}
                </div>
            )}
            {Object.keys(report.by_payment_method).length > 0 && (
                <div className="mt-2">
                    <div className="mb-1 text-[10px] uppercase text-muted-foreground">Payment methods</div>
                    {Object.entries(report.by_payment_method).map(([k, v]) => (
                        <div key={k} className="flex justify-between text-[11px]">
                            <span className="capitalize">{k.replace("_", " ")}</span>
                            <span className="font-medium">{formatCurrency(v)}</span>
                        </div>
                    ))}
                </div>
            )}
            {report.top_products.length > 0 && (
                <div className="mt-2">
                    <div className="mb-1 text-[10px] uppercase text-muted-foreground">Top items</div>
                    {report.top_products.slice(0, 4).map((t) => (
                        <div key={t.name} className="flex justify-between text-[11px]">
                            <span className="truncate">{t.name}</span>
                            <span className="font-medium">{t.quantity} · {formatCurrency(t.revenue)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ------------------------------------------------------------
// Invoice modal (printable)
// ------------------------------------------------------------
function InvoiceModal({ invoice, onClose }) {
    const print = () => {
        window.print();
    };
    return (
        <Modal title={`Invoice ${invoice.invoice_number}`} onClose={onClose} width="max-w-md">
            <div className="space-y-3 text-[13px]" data-testid="pos-invoice-modal">
                <div>
                    <div className="text-base font-semibold">{invoice.hotel.name}</div>
                    <div className="text-[11px] text-muted-foreground">{invoice.hotel.address || ""}</div>
                    <div className="text-[11px] text-muted-foreground">VAT: {invoice.hotel.vat_number || "—"}</div>
                </div>
                <div className="border-t border-border pt-2">
                    <div className="text-[11px] text-muted-foreground">{invoice.outlet}{invoice.table ? ` · ${invoice.table}` : ""}</div>
                    <div className="text-[11px] text-muted-foreground">{formatDateTime(invoice.issued_at)}</div>
                </div>
                {invoice.guest && (
                    <div className="text-[12px]">Guest: {invoice.guest.first_name} {invoice.guest.last_name}</div>
                )}
                <div className="space-y-1 border-t border-border pt-2">
                    {invoice.items.map((it, i) => (
                        <div key={i} className="flex justify-between text-[12px]">
                            <span>{it.quantity}× {it.name}</span>
                            <span>{formatCurrency(it.price * it.quantity)}</span>
                        </div>
                    ))}
                </div>
                <div className="space-y-0.5 border-t border-border pt-2">
                    <Row label="Subtotal" value={formatCurrency(invoice.subtotal)} />
                    {invoice.discount_amount > 0 && <Row label="Discount" value={`-${formatCurrency(invoice.discount_amount)}`} />}
                    <Row label="Tax (10%)" value={formatCurrency(invoice.tax)} />
                    <Row label="Total" value={formatCurrency(invoice.total)} bold />
                </div>
                <div className="border-t border-border pt-2">
                    <div className="text-[11px] uppercase text-muted-foreground">Payments</div>
                    {invoice.payments.map((p, i) => (
                        <div key={i} className="flex justify-between text-[12px]">
                            <span className="capitalize">{p.method.replace("_", " ")}</span>
                            <span>{formatCurrency(p.amount)}</span>
                        </div>
                    ))}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="akm-btn-ghost">Close</button>
                    <button onClick={print} className="akm-btn-primary"><Printer size={12} /> Print</button>
                </div>
            </div>
        </Modal>
    );
}

// ------------------------------------------------------------
// Manage drawer — Products / Tables CRUD
// ------------------------------------------------------------
function ManageDrawer({ products, tables, reload, onClose }) {
    const [tab, setTab] = useState("products");
    return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="pos-manage-drawer">
            <div className="absolute inset-0 bg-foreground/30" onClick={onClose} />
            <div className="relative h-full w-full max-w-[720px] overflow-y-auto bg-background shadow-xl">
                <div className="sticky top-0 z-10 border-b border-border bg-background/95 px-5 py-3.5 backdrop-blur">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold">Manage POS</div>
                        <button onClick={onClose} className="akm-btn-ghost text-[11px]">Close</button>
                    </div>
                    <div className="mt-3 flex items-center gap-1 border-b border-border">
                        {[{ k: "products", label: "Products" }, { k: "tables", label: "Tables" }].map((t) => (
                            <button key={t.k} onClick={() => setTab(t.k)}
                                    data-testid={`manage-tab-${t.k}`}
                                    className={`-mb-px border-b-2 px-3 py-2 text-[12px] font-medium ${
                                        tab === t.k ? "border-[#ff7b02] text-[#ff7b02]" : "border-transparent text-muted-foreground"
                                    }`}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="px-5 py-4">
                    {tab === "products" && <ProductsManage products={products} reload={reload} />}
                    {tab === "tables" && <TablesManage tables={tables} reload={reload} />}
                </div>
            </div>
        </div>
    );
}

function ProductsManage({ products, reload }) {
    const [open, setOpen] = useState(false);
    const remove = async (id) => {
        if (!window.confirm("Delete this product?")) return;
        await api.delete(`/pos/products/${id}`);
        toast.success("Deleted"); await reload();
    };
    const toggle = async (p) => {
        await api.patch(`/pos/products/${p.id}`, { active: !p.active });
        await reload();
    };
    return (
        <div>
            <div className="mb-2 flex justify-end">
                <button onClick={() => setOpen(true)} className="akm-btn-primary" data-testid="manage-new-product">
                    <Plus size={13} /> New product
                </button>
            </div>
            <div className="akm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Outlet</th><th className="px-3 py-2 text-left">Category</th><th className="px-3 py-2 text-right">Price</th><th className="px-3 py-2 text-left">Status</th><th /></tr>
                    </thead>
                    <tbody>
                        {products.map((p) => (
                            <tr key={p.id} className="border-t border-border">
                                <td className="px-3 py-2 font-medium">{p.name}</td>
                                <td className="px-3 py-2 text-[12px] capitalize">{p.outlet.replace("_", " ")}</td>
                                <td className="px-3 py-2 text-[12px] text-muted-foreground">{p.category}</td>
                                <td className="px-3 py-2 text-right">{formatCurrency(p.price)}</td>
                                <td className="px-3 py-2">
                                    <button onClick={() => toggle(p)} className={`rounded-md px-2 py-0.5 text-[10px] font-medium ${p.active ? "bg-emerald-50 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
                                        {p.active ? "Active" : "Inactive"}
                                    </button>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => remove(p.id)} className="akm-btn-ghost text-[10px] text-red-700"><Trash2 size={10} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {open && <ProductForm onClose={() => setOpen(false)} onSaved={async () => { setOpen(false); await reload(); }} />}
        </div>
    );
}
function ProductForm({ onClose, onSaved }) {
    const [f, setF] = useState({ outlet: "restaurant", name: "", category: "", price: 0, taxable: true, active: true });
    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/pos/products", f);
            toast.success("Created");
            onSaved();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    return (
        <Modal title="New product" onClose={onClose}>
            <form onSubmit={submit} className="space-y-3">
                <Field label="Outlet">
                    <select className="akm-input" value={f.outlet} onChange={(e) => setF({ ...f, outlet: e.target.value })}>
                        {OUTLET_KEYS.map((k) => <option key={k} value={k}>{OUTLET_META[k].label}</option>)}
                    </select>
                </Field>
                <Field label="Name"><input required className="akm-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="product-name" /></Field>
                <Field label="Category"><input required className="akm-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Drinks, Mains, …" /></Field>
                <Field label="Price"><input required type="number" step="0.01" className="akm-input" value={f.price} onChange={(e) => setF({ ...f, price: parseFloat(e.target.value) || 0 })} /></Field>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">Cancel</button>
                    <button type="submit" className="akm-btn-primary" data-testid="product-submit">Create</button>
                </div>
            </form>
        </Modal>
    );
}

function TablesManage({ tables, reload }) {
    const [open, setOpen] = useState(false);
    const remove = async (id) => {
        if (!window.confirm("Delete this table?")) return;
        await api.delete(`/pos/tables/${id}`);
        toast.success("Deleted"); await reload();
    };
    return (
        <div>
            <div className="mb-2 flex justify-end">
                <button onClick={() => setOpen(true)} className="akm-btn-primary" data-testid="manage-new-table">
                    <Plus size={13} /> New table
                </button>
            </div>
            <div className="akm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Outlet</th><th className="px-3 py-2 text-left">Location</th><th className="px-3 py-2 text-right">Capacity</th><th /></tr>
                    </thead>
                    <tbody>
                        {tables.map((t) => (
                            <tr key={t.id} className="border-t border-border">
                                <td className="px-3 py-2 font-medium">{t.name}</td>
                                <td className="px-3 py-2 text-[12px] capitalize">{t.outlet.replace("_", " ")}</td>
                                <td className="px-3 py-2 text-[12px] text-muted-foreground">{t.location || "—"}</td>
                                <td className="px-3 py-2 text-right">{t.capacity}</td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => remove(t.id)} className="akm-btn-ghost text-[10px] text-red-700"><Trash2 size={10} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {open && <TableForm onClose={() => setOpen(false)} onSaved={async () => { setOpen(false); await reload(); }} />}
        </div>
    );
}
function TableForm({ onClose, onSaved }) {
    const [f, setF] = useState({ outlet: "restaurant", name: "", capacity: 4, location: "", active: true });
    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/pos/tables", f);
            toast.success("Created");
            onSaved();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    return (
        <Modal title="New table" onClose={onClose}>
            <form onSubmit={submit} className="space-y-3">
                <Field label="Outlet">
                    <select className="akm-input" value={f.outlet} onChange={(e) => setF({ ...f, outlet: e.target.value })}>
                        {OUTLET_KEYS.map((k) => <option key={k} value={k}>{OUTLET_META[k].label}</option>)}
                    </select>
                </Field>
                <Field label="Name"><input required className="akm-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="table-name" /></Field>
                <div className="grid grid-cols-2 gap-2">
                    <Field label="Capacity"><input type="number" min="1" className="akm-input" value={f.capacity} onChange={(e) => setF({ ...f, capacity: parseInt(e.target.value, 10) || 1 })} /></Field>
                    <Field label="Location"><input className="akm-input" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Patio" /></Field>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">Cancel</button>
                    <button type="submit" className="akm-btn-primary" data-testid="table-submit">Create</button>
                </div>
            </form>
        </Modal>
    );
}
